import React, { useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  InputAdornment,
  FormLabel,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import StatusRadioGroup from "../../component/ui/FormFields/StatusRadioGroup";
import TextInputField from "../../component/ui/FormFields/TextInputField";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCustomerDetailsRequest,
  resendCustomerPasswordRequest,
  clearResendPasswordStatus,
  convertRoleRequest,
  clearConvertRoleStatus,
} from "../../redux/Customers/actions/customerActions";
import CustomButton from "../../component/ui/FormFields/Button";
import Loading from "../../component/ui/Loading";
import ConvertCustomerToVendorModal from "./components/ConvertCustomerToVendorModal";
import { clearConvertCustomerToVendorStatus } from "../../redux/Vendors/actions/vendorActions";

import {
  CUSTOMERS,
  EDIT_CUSTOMER,
} from "../../component/common/Routes/AppRoutes";
import { useForm } from "react-hook-form";
import { VENDOR_EDIT_CUSTOMER } from "../../component/common/Routes/VendorRoutes";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PublicIcon from "@mui/icons-material/Public";
import PinDropIcon from "@mui/icons-material/PinDrop";
import FlagIcon from "@mui/icons-material/Flag";
import CakeIcon from "@mui/icons-material/Cake";
import BadgeIcon from "@mui/icons-material/Badge";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import { Notifications, WhatsApp, Message } from "@mui/icons-material";
import GenderRadioGroup from "../../component/ui/FormFields/GenderRadioGroup";
import { formatDateWithAmPm } from "../../Style";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ViewCustomer = () => {
  const { customerId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { control } = useForm();
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();

  const page = location?.state?.page ?? 1;
  const searchQuery = location?.state?.searchQuery ?? "";
  const [openConvertModal, setOpenConvertModal] = React.useState(false);

  const convertRoleState = useSelector(
    (state) =>
      state.customers.convertRole || {
        loading: false,
        success: false,
        error: null,
      },
  );

  const convertState = useSelector(
    (state) =>
      state.vendors.convertCustomerToVendor || {
        loading: false,
        success: false,
        error: null,
      },
  );
  console.log("convertState", convertState);
  const permissions = useMemo(() => {
    let safePermissions = {};

    try {
      if (user?.permissions) {
        safePermissions =
          typeof user.permissions === "string"
            ? JSON.parse(user.permissions)
            : user.permissions;
      }
    } catch (error) {
      safePermissions = {};
    }

    return safePermissions;
  }, [user]);

  const customerDetailsState = useSelector(
    (state) => state.customers.customerDetails,
  );

  const resendPasswordState = useSelector(
    (state) =>
      state.customers.resendPassword || {
        loading: false,
        success: false,
        error: null,
      },
  );

  const customerDetails = customerDetailsState?.data || {};
  const viewLoading = customerDetailsState?.loading || false;
  const error = customerDetailsState?.error || null;
  const statusValue = customerDetails.data?.status;
  const status = statusValue === 1 ? "Active" : "Inactive";
  const genderValue = customerDetails.data?.familyMember?.gender_type || "";

  useEffect(() => {
    if (customerId) {
      dispatch(fetchCustomerDetailsRequest(customerId));
    }
  }, [dispatch, customerId]);

  useEffect(() => {
    if (convertState.success) {
      toast.success(
        convertState.message || "Converted to Vendor successfully!",
        {
          position: "top-center",
          autoClose: 3000,
          onClose: () => {
            setOpenConvertModal(false);
            dispatch(clearConvertCustomerToVendorStatus());
            navigate("/customers");
          },
        },
      );
    }

    if (convertState.error) {
      toast.error(`Error: ${convertState.error}`, {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          dispatch(clearConvertCustomerToVendorStatus());
        },
      });
    }
  }, [convertState, dispatch, navigate]);

  useEffect(() => {
    if (convertRoleState.success) {
      toast.success(
        convertRoleState.message || "Role converted successfully!",
        {
          position: "top-center",
          autoClose: 3000,
          onClose: () => {
            dispatch(clearConvertRoleStatus());
            navigate("/customers");
          },
        },
      );
    }

    if (convertRoleState.error) {
      toast.error(`Error: ${convertRoleState.error}`, {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          dispatch(clearConvertRoleStatus());
        },
      });
    }
  }, [convertRoleState, dispatch, navigate]);

  useEffect(() => {
    if (resendPasswordState.success) {
      toast.success("Password resend sent successfully!", {
        position: "top-center",
        autoClose: 3000,
      });
      setTimeout(() => {
        dispatch(clearResendPasswordStatus());
      }, 3000);
    }

    if (resendPasswordState.error) {
      toast.error(`Error: ${resendPasswordState.error}`, {
        position: "top-center",
        autoClose: 3000,
      });
      setTimeout(() => {
        dispatch(clearResendPasswordStatus());
      }, 3000);
    }
  }, [resendPasswordState, dispatch]);

  if (viewLoading) {
    return <Loading text="Loading Customer details..." />;
  }

  if (error || !Object.keys(customerDetails).length) {
    return (
      <Box textAlign="center" py={5}>
        <Typography variant="h6" color="error">
          {error || "No Customer data found!"}
        </Typography>
        <CustomButton
          text="Back to Customers"
          onClick={() => navigate(CUSTOMERS)}
        />
      </Box>
    );
  }

  const fields = [
    {
      label: "First Name",
      value: customerDetails.data.first_name || "-",
      xs: 12,
      sm: 6,
      icon: <PersonIcon />,
    },
    {
      label: "Last Name",
      value: customerDetails.data.last_name || "-",
      xs: 12,
      sm: 6,
      icon: <PersonIcon />,
    },
    {
      label: "Email Address",
      value: customerDetails.data.email || "-",
      xs: 12,
      sm: 6,
      icon: <EmailIcon />,
    },
    {
      label: "Country Code",
      value: customerDetails.data.country_code || "-",
      xs: 4,
      sm: 2,
      icon: <FlagIcon />,
    },
    {
      label: "Mobile Number",
      value: customerDetails.data.phone || "-",
      xs: 8,
      sm: 4,
      icon: <PhoneIcon />,
    },
    {
      label: "Date of Birth",
      value: formatDateWithAmPm(customerDetails.data?.familyMember?.dob) || "-",
      xs: 12,
      sm: 6,
      icon: <CakeIcon />,
    },
    {
      label: "Nationality",
      value: customerDetails.data?.familyMember?.nationality || "-",
      xs: 12,
      sm: 6,
      icon: <FlagIcon />,
    },
    {
      label: "Passport Number",
      value: customerDetails.data?.familyMember?.passport_number || "-",
      xs: 12,
      sm: 6,
      icon: <BadgeIcon />,
    },
    {
      label: "Aadhar Number",
      value: customerDetails.data?.familyMember?.aadhar_number || "-",
      xs: 12,
      sm: 6,
      icon: <FingerprintIcon />,
    },
    {
      label: "Address 1",
      value: customerDetails.data.address1 || "-",
      xs: 12,
      sm: 6,
      icon: <HomeIcon />,
    },
    {
      label: "Address 2",
      value: customerDetails.data.address2 || "-",
      xs: 12,
      sm: 6,
      icon: <HomeIcon />,
    },
    {
      label: "Country",
      value: customerDetails.data.country_name || "-",
      xs: 12,
      sm: 6,
      icon: <PublicIcon />,
    },
    {
      label: "State",
      value: customerDetails.data.state_name || "-",
      xs: 12,
      sm: 6,
      icon: <LocationCityIcon />,
    },
    {
      label: "City",
      value: customerDetails.data.city_name || "-",
      xs: 12,
      sm: 6,
      icon: <LocationCityIcon />,
    },
    {
      label: "Pincode",
      value: customerDetails.data.pincode || "-",
      xs: 12,
      sm: 6,
      icon: <PinDropIcon />,
    },
    {
      label: "Gst Number",
      value: customerDetails.data.customer_gst_no || "-",
      xs: 12,
      sm: 6,
      icon: <BadgeIcon />,
    },
    {
      label: "PAN Number",
      value: customerDetails?.data?.familyMember?.pan_number || "-",
      xs: 12,
      sm: 6,
      icon: <BadgeIcon />,
    },
  ];

  // Get notification preferences - default to enabled (1) if not specified
  const notificationEmail = customerDetails.data?.notification_email !== 2;
  const notificationSMS = customerDetails.data?.notification_sms !== 2;
  const notificationWhatsApp = customerDetails.data?.notification_whatsup !== 2;

  return (
    <>
      <ToastContainer />
      <Box className="container">
        <Card>
          <CardContent sx={{ padding: 4 }}>
            <Typography variant="h5" className="form-heading" gutterBottom>
              View Customer
            </Typography>
            <Grid
              container
              rowSpacing={4}
              columnSpacing={{ xs: 1, sm: 1, md: 1, lg: 3 }}
              className="customer-form-margin"
            >
              {fields.map((field, index) => (
                <Grid item xs={field.xs} sm={field.sm} key={index}>
                  <TextInputField
                    label={field.label}
                    value={field.value}
                    disabled={true}
                    startAdornment={
                      <InputAdornment position="start">
                        {field.icon}
                      </InputAdornment>
                    }
                  />
                </Grid>
              ))}

              <Grid item xs={12} sm={6}>
                <GenderRadioGroup
                  name="gender"
                  control={control}
                  defaultValue={genderValue}
                  disabled={true}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StatusRadioGroup
                  name={"status"}
                  control={control}
                  defaultValue={status}
                  disabled={true}
                />
              </Grid>

              {/* Notification Preferences Section */}
              <Grid item xs={12}>
                <Box sx={{ mt: 2, mb: 2 }}>
                  <FormLabel
                    component="legend"
                    sx={{ fontWeight: "bold", mb: 2 }}
                  >
                    <Notifications sx={{ mr: 1, verticalAlign: "middle" }} />
                    Notification Preferences
                  </FormLabel>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={notificationEmail}
                            disabled={true}
                            color="primary"
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <EmailIcon sx={{ mr: 1, fontSize: 20 }} />
                            Email Notifications
                          </Box>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={notificationSMS}
                            disabled={true}
                            color="primary"
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Message sx={{ mr: 1, fontSize: 20 }} />
                            SMS Notifications
                          </Box>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={notificationWhatsApp}
                            disabled={true}
                            color="primary"
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <WhatsApp sx={{ mr: 1, fontSize: 20 }} />
                            WhatsApp Notifications
                          </Box>
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
            {/* Buttons */}
            <Grid item xs={12}>
              <Box
                className="button-container"
                sx={{ justifyContent: "space-between" }}
              >
                <Box>
                  {customerDetails.data.current_role === 3 &&
                  !customerDetails.data.is_converted ? (
                    <CustomButton
                      variant="contained"
                      className="success-button"
                      text="Convert to Vendor"
                      onClick={() => setOpenConvertModal(true)}
                    />
                  ) : (
                    <CustomButton
                      variant="contained"
                      className="success-button"
                      text="Convert to Vendor"
                      loading={convertRoleState.loading}
                      onClick={() => {
                        dispatch(
                          convertRoleRequest({
                            customer_id: customerDetails.data.id,
                          }),
                        );
                      }}
                    />
                  )}
                </Box>

                <Box sx={{ display: "flex", gap: "16px" }}>
                  <CustomButton
                    variant="contained"
                    className="cancel-button"
                    type="button"
                    text={"Back"}
                    onClick={() =>
                      navigate("/customers", {
                        state: { page: page, searchQuery: searchQuery },
                      })
                    }
                  />

                  {(permissions?.customers?.edit ||
                    user?.user_type === 1 ||
                    user?.user_type === 2) && (
                    <>
                      <CustomButton
                        variant="contained"
                        className="success-button"
                        text="Edit"
                        onClick={() => {
                          const editRoute =
                            user.role === "Vendor"
                              ? VENDOR_EDIT_CUSTOMER.replace(
                                  ":customerId",
                                  customerDetails.data.id,
                                )
                              : EDIT_CUSTOMER.replace(
                                  ":customerId",
                                  customerDetails.data.id,
                                );
                          navigate(editRoute);
                        }}
                      />
                      <CustomButton
                        variant="contained"
                        className="grey-button"
                        text="Resend Password"
                        disabled={resendPasswordState.loading}
                        onClick={() =>
                          dispatch(
                            resendCustomerPasswordRequest(
                              customerDetails.data.email,
                            ),
                          )
                        }
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Grid>
          </CardContent>
        </Card>
        <ConvertCustomerToVendorModal
          open={openConvertModal}
          onClose={() => setOpenConvertModal(false)}
          customerId={customerId}
          customerDetails={customerDetails}
          loading={convertState.loading}
        />
      </Box>
    </>
  );
};

export default ViewCustomer;
